import React from 'react';
import { Link } from 'react-router-dom';
import { Event } from '../types';
import SportIcon from './icons/SportIcon';
import { getCategoryLabel, getStatusLabel } from '../utils/eventUtils';
import styles from './EventCard.module.css';

export type EventCardVariant = 'row' | 'carousel';

interface EventCardProps {
    event: Event;
    variant?: EventCardVariant;
    isFavorite?: boolean;
    hasUpvoted?: boolean;
    onUpvote?: (e: React.MouseEvent, eventId: string) => void;
    onToggleFavorite?: (e: React.MouseEvent, eventId: string) => void;
}

const EventCard: React.FC<EventCardProps> = ({
    event,
    variant = 'row',
    isFavorite = false,
    hasUpvoted = false,
    onUpvote,
    onToggleFavorite,
}) => {
    const isLive = event.status === 'live';
    const isCompleted = event.status === 'completed';
    const isCricket = event.category === 'Cricket';

    const statusClass =
        isLive ? styles.live : isCompleted ? styles.completed : styles.scheduled;

    const sportClass = isCricket ? styles.cricketCard : styles.footballCard;
    const variantClass = variant === 'carousel' ? styles.carousel : styles.row;

    const metaLine = [event.date, event.time, event.location].filter(Boolean).join(' · ');

    return (
        <article
            className={`${styles.card} ${sportClass} ${variantClass} ${isLive ? styles.isLive : ''} ${isCompleted ? styles.isCompleted : ''}`}
        >
            <Link to={`/event/${event.id}`} className={styles.link}>
                <div className={styles.iconWrap} aria-hidden="true">
                    <SportIcon category={event.category} size={variant === 'carousel' ? 22 : 18} />
                </div>

                <div className={styles.main}>
                    <div className={styles.topRow}>
                        <span className={`${styles.category} ${isCricket ? styles.cricket : styles.football}`}>
                            {getCategoryLabel(event.category)}
                        </span>
                        <span className={`${styles.status} ${statusClass}`}>
                            {isLive && <span className={styles.liveDot} />}
                            {getStatusLabel(event.status)}
                        </span>
                    </div>

                    <h3 className={styles.title}>{event.name}</h3>
                    <p className={styles.meta}>{metaLine}</p>
                </div>

                <div className={styles.trailing}>
                    {isLive ? (
                        <span className={styles.watchPill}>দেখুন</span>
                    ) : (
                        <span className={styles.chevron} aria-hidden="true">›</span>
                    )}
                </div>
            </Link>

            {(onUpvote || onToggleFavorite) && (
                <div className={styles.toolbar}>
                    {onUpvote && (
                        <button
                            type="button"
                            className={`${styles.toolbarBtn} ${hasUpvoted ? styles.disabled : ''}`}
                            onClick={(e) => onUpvote(e, event.id)}
                            disabled={hasUpvoted}
                            aria-label="আপভোট"
                        >
                            <span className={styles.toolbarIcon} aria-hidden="true">↑</span>
                            <span>{event.upvotes || 0}</span>
                        </button>
                    )}
                    {onToggleFavorite && (
                        <button
                            type="button"
                            className={`${styles.toolbarBtn} ${isFavorite ? styles.favoriteActive : ''}`}
                            onClick={(e) => onToggleFavorite(e, event.id)}
                            aria-label={isFavorite ? 'পছন্দ থেকে সরান' : 'পছন্দে যোগ করুন'}
                        >
                            <span className={styles.toolbarIcon} aria-hidden="true">{isFavorite ? '★' : '☆'}</span>
                        </button>
                    )}
                </div>
            )}
        </article>
    );
};

export default EventCard;
